import React from 'react';

import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  CircularProgress,
  TextField,
} from '@mui/material';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';

import { tokens } from '../../theme/styles/themeMui';

export type AutocompleteOption = {
  title: string;
  value: string;
};

type BaseAutocompleteProps = Omit<
  MuiAutocompleteProps<AutocompleteOption, false, boolean, false>,
  'renderInput' | 'getOptionLabel' | 'isOptionEqualToValue'
>;

export type AutocompleteProps = BaseAutocompleteProps & {
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  required?: boolean;
  name?: string;
};

const StyledAutocomplete = styled(MuiAutocomplete<AutocompleteOption, false, boolean, false>)(
  ({ theme, fullWidth }) => ({
    width: fullWidth ? '100%' : 'min(400px, 100%)',
    [`& .MuiInputLabel-root`]: {
      ...tokens.typography['body-s-accent'],
      position: 'unset',
      transform: 'none',
      color: theme.palette.colors.content.primary,
    },
    [`& .${inputLabelClasses.root}.Mui-focused`]: {
      color: theme.palette.colors.content.primary,
    },
    [`& .${inputLabelClasses.root}.Mui-error`]: {
      color: theme.palette.colors.content.primary,
    },
    [`& .${inputBaseClasses.root}`]: {
      padding: '12px !important',
      paddingRight: '48px !important',
      gap: '12px',
      flexWrap: 'wrap',
      height: 'auto',
      minHeight: '48px',
    },
    [`& .${inputBaseClasses.root} fieldset`]: {
      borderRadius: '8px',
      borderWidth: '2px',
      borderColor: theme.palette.colors.special.nulled,
      background: theme.palette.colors.bg['neutral-secondary'],
    },
    [`& .${inputBaseClasses.root}.Mui-focused fieldset`]: {
      background: theme.palette.colors.special.nulled,
    },
    [`& .${inputBaseClasses.root}:not(.Mui-error):hover fieldset`]: {
      borderColor: theme.palette.colors.border['action-primary'],
    },
    [`& .${inputBaseClasses.root}.Mui-error fieldset`]: {
      borderColor: theme.palette.colors.border['danger-primary'],
    },
    [`& .${inputBaseClasses.root} input`]: {
      ...tokens.typography.bodyMRegular,
      padding: '0px !important',
      color: theme.palette.colors.content.primary,
    },
    [`& .${inputBaseClasses.root} textarea`]: {
      ...tokens.typography.bodyMRegular,
      padding: '0px !important',
      color: theme.palette.colors.content.primary,
      background: 'transparent',
    },
    [`& .MuiAutocomplete-endAdornment`]: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    [`& .MuiFormHelperText-root`]: {
      marginLeft: '0px',
      ...tokens.typography.bodySRegular,
      color: theme.palette.colors.content.secondary,
    },
    [`& .MuiFormHelperText-root.Mui-error`]: {
      color: theme.palette.colors.content['danger-primary'],
    },
  }),
);

const StyledTextField = styled(TextField)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  [`& .MuiInputLabel-root`]: {
    ...tokens.typography['body-s-accent'],
    position: 'unset',
    transform: 'none',
    color: theme.palette.colors.content.primary,
  },
  [`& .${inputLabelClasses.root}.Mui-focused`]: {
    color: theme.palette.colors.content.primary,
  },
  [`& .${inputLabelClasses.root}.Mui-error`]: {
    color: theme.palette.colors.content.primary,
  },
  [`& fieldset legend`]: {
    display: 'none',
  },
}));

export function Autocomplete({
  label,
  placeholder,
  helperText,
  error,
  required,
  name,
  loading,
  ...props
}: AutocompleteProps) {
  return (
    <StyledAutocomplete
      {...props}
      loading={loading}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          multiline
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          name={name}
          slotProps={{
            inputLabel: { ...params.InputLabelProps, shrink: true, required },
            htmlInput: { ...params.inputProps, required: false },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            marginTop: '4px',
            borderRadius: '8px',
            boxShadow:
              '0 0 1px 1px rgba(26, 27, 35, 0.04), 0 16px 16px 0 rgba(26, 27, 35, 0.06), 0 16px 16px -16px rgba(26, 27, 35, 0.24)',
          },
        },
        listbox: {
          sx: {
            maxHeight: '300px',
            overflowY: 'auto',
            scrollBehavior: 'smooth',
          },
        },
      }}
    />
  );
}
